import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ChatService } from '@services/chat-service/chat-service';
import {
  MULTI_SELECT_SEPARATOR,
  QuizAnswerOutcome,
  QuizChatConfig,
  QuizChatMessage,
  QuizChatService,
  QuizOption,
  QuizQuestionLayout,
} from '@services/quiz-chat-service';
import { ConnectionService } from '@services/connection-service';
import { ConsultationSessionService } from '@services/consultation-session-service';
import { ExperimentService } from '@services/experiment';
import { EventLoggerService } from '@services/event-logger-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { InputType, SingleSelectorService } from '@shared/single-selector/single-selector.service';
import { QuizProgressHeaderComponent } from '@components/quiz/quiz-progress-header/quiz-progress-header.component';
import { QuizQuestionHeaderComponent } from '@components/quiz/quiz-question-header/quiz-question-header.component';
import { QuizOptionListComponent, QuizOptionListLayout } from '@components/quiz/quiz-option-list/quiz-option-list.component';
import { QuizImageOptionGridComponent } from '@components/quiz/quiz-image-option-grid/quiz-image-option-grid.component';
import { QuizTextAnswerComponent } from '@components/quiz/quiz-text-answer/quiz-text-answer.component';
import { QuizFooterCtaComponent } from '@components/quiz/quiz-footer-cta/quiz-footer-cta.component';
import { QuizPhotoUploadComponent } from '@components/quiz/quiz-photo-upload/quiz-photo-upload.component';
import { LoadingModule } from '@components/loading';
import { AppConfig } from 'client/app/app.config';

const CONSULTATION_SESSION_TYPE = 'consultationSession';
const ANALYTICS_PREFIX = 'consultation_quiz';
// Questions the quiz never shows the user: `Welcome` (auto-clicks its "Get Started" TREE button)
// and `whatDoCureSkinMembersSay` (the inputless tree-end testimonial — kept polling past, not rendered).
const AUTO_SKIP_QUESTION_IDS: readonly string[] = ['Welcome', 'whatDoCureSkinMembersSay'];
const IOS_OS_NAME = 'iOS';
// Single-select question that renders its language options as a 2-up grid (chatV2 renders it likewise).
const COMMUNICATION_LANGUAGE_QUESTION_ID = 'communicationLanguagePreference';
// Image-option questions whose ordering conveys severity — each card gets a 1-based position badge.
const HAIRFALL_STAGE_QUESTION_IDS: readonly string[] = ['MaleHairfallStages', 'FemaleHairfallStages'];
// Analytics event names carried over from chatV2 for parity.
const CLICKED_BACK_EVENT = 'clickedBack';

@Component({
  selector: 'app-consultation-quiz',
  templateUrl: './consultation-quiz.html',
  standalone: true,
  imports: [
    QuizProgressHeaderComponent,
    QuizQuestionHeaderComponent,
    QuizOptionListComponent,
    QuizImageOptionGridComponent,
    QuizTextAnswerComponent,
    QuizFooterCtaComponent,
    QuizPhotoUploadComponent,
    LoadingModule,
  ],
  providers: [ChatService, QuizChatService, SingleSelectorService],
})
export class ConsultationQuizComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly connectionService = inject(ConnectionService);
  private readonly consultationSessionService = inject(ConsultationSessionService);
  private readonly experimentService = inject(ExperimentService);
  private readonly eventLogger = inject(EventLoggerService);
  private readonly appBridge = inject(AppWebBridgeService);
  private readonly singleSelectorService = inject(SingleSelectorService);
  private readonly appConfig = inject(AppConfig);
  readonly quizService = inject(QuizChatService);

  readonly QuizQuestionLayout = QuizQuestionLayout;
  readonly QuizOptionListLayout = QuizOptionListLayout;

  readonly consultationSession = signal<Table.ConsultationSession | null>(null);
  readonly showVoice = signal<boolean>(false);
  private readonly selectedIndices = signal<number[]>([]);
  private readonly typedText = signal<string>('');
  // Fixed question count for the class (experiment-configured, gender/age resolved); set in ngOnInit.
  private readonly stepTotal = signal<number>(1);

  private readonly continueLabel = $localize`:@@continue:Continue`;
  private readonly cameraIconClass = 'cs-icon-camera tw-text-500 tw-mt-1';

  private readonly syncQuestionForLanguageBanner = effect((): void => {
    this.quizService.questionText(); // reactive trigger only — currentQuestion() is a method, not a signal
    const questionId = this.quizService.currentQuestion()?.get('questionId');
    const regimenClass = this.consultationSession()?.get('PrivateMainConcernClass');
    if (questionId && regimenClass) this.consultationSessionService.setLastBotQuestionId(regimenClass, questionId);
  });

  private readonly currentStep = computed(() => Math.min(this.quizService.questionNumber(), this.stepTotal()));
  readonly stepLabel = computed(() => `${this.currentStep()}/${this.stepTotal()}`);
  // True ratio (not the chatV2 accumulator) — HAIR trees are fixed-length, so current/total is exact.
  readonly progressPercent = computed(() => Math.round((this.currentStep() / this.stepTotal()) * 100));
  readonly showQuestion = computed(() => this.quizService.hasActiveQuestion() && !this.quizService.isCompleted());
  // Loader covers every pre-question / transition state (incl. reload) — anything that isn't an
  // active question or completion, so we never render a blank body.
  readonly showLoading = computed(() => !this.quizService.hasActiveQuestion() && !this.quizService.isCompleted());
  readonly inputsDisabled = computed(() => this.quizService.isThinking());
  // An answer is in flight and we're waiting for the next question — drives the CTA spinner.
  readonly isSubmittingAnswer = computed(() => this.quizService.isThinking());
  readonly allSelectableOptions = computed(() => [...this.quizService.imageOptions(), ...this.quizService.buttonOptions()]);
  // currentQuestion() is a plain field, not a signal — key off questionText() (set per new question)
  // so these recompute when the question actually changes.
  private readonly currentQuestionId = computed(() => {
    this.quizService.questionText();
    return this.quizService.currentQuestion()?.get('questionId');
  });
  readonly optionListType = computed(() => (
    this.quizService.layout() === QuizQuestionLayout.MultiSelect
    || this.currentQuestionId() === COMMUNICATION_LANGUAGE_QUESTION_ID
      ? QuizOptionListLayout.Grid
      : QuizOptionListLayout.Stack
  ));
  readonly showImageOptionIndex = computed(() => HAIRFALL_STAGE_QUESTION_IDS.includes(this.currentQuestionId()));
  readonly ctaLabel = computed(() => {
    if (this.quizService.layout() === QuizQuestionLayout.Text) {
      // Typed text submits as "Continue"; an empty box falls back to the "None" TREE button.
      if (this.typedText().trim()) return this.continueLabel;
      return this.quizService.textCtaOption()?.text || this.continueLabel;
    }
    if (this.quizService.layout() === QuizQuestionLayout.PhotoUpload) {
      return this.quizService.photoCtaOption()?.text || this.continueLabel;
    }
    return this.continueLabel;
  });
  readonly ctaIcon = computed(() => (
    this.quizService.layout() === QuizQuestionLayout.PhotoUpload ? this.cameraIconClass : ''
  ));
  readonly ctaDisabled = computed(() => {
    if (this.inputsDisabled()) return true;
    if (this.quizService.layout() === QuizQuestionLayout.Text) {
      return !this.typedText().trim() && !this.quizService.textCtaOption();
    }
    if (this.quizService.layout() === QuizQuestionLayout.PhotoUpload) {
      return false;
    }
    return this.selectedIndices().length === 0;
  });

  private user: Table.User;
  private completionHandled = false;

  constructor() {
    effect(() => {
      if (this.quizService.isCompleted()) this.handleCompletion();
    });
    effect(() => {
      if (this.quizService.requiresFallback()) this.navigateToLegacyChat();
    });
    effect(() => {
      this.quizService.questionText();
      this.selectedIndices.set([]);
      this.typedText.set('');
    });
  }

  async ngOnInit(): Promise<void> {
    const screenLoadStart = Date.now();
    this.appBridge.setStatusBarColor('white');
    this.eventLogger.cleverTapEvent('impression', JSON.stringify({ name: ANALYTICS_PREFIX }));
    this.user = this.connectionService.getActingUser();
    const objectId: string = this.route.snapshot.params.id;
    const pendingSession = this.consultationSessionService.getPendingConsultationSession();
    const session = pendingSession?.id === objectId
      ? pendingSession
      : (await this.connectionService.fetchConsultationSessions({ where: { objectId } }))[0];
    this.consultationSession.set(session || null);
    if (!session) return;

    await this.experimentService.experimentsLoaded;
    this.stepTotal.set(this.experimentService.getConsultationQuizStepCount(session.get('PrivateMainConcernClass')));
    await this.resolveVoiceVisibility();
    this.quizService.configure(this.buildQuizConfig(session));
    await this.quizService.start();
    this.eventLogger.trackEvent('chat_screen_time', { timeInMillSec: Date.now() - screenLoadStart });
  }

  onSelectionChange(indices: number[]): void {
    this.selectedIndices.set(indices);
  }

  /** Button rows share the combined option space with the image grid (images first). */
  onButtonSelectionChange(indices: number[]): void {
    const offset = this.quizService.imageOptions().length;
    this.selectedIndices.set(indices.map((index) => index + offset));
  }

  onImageSelectionChange(index: number): void {
    this.selectedIndices.set([index]);
  }

  onTypedTextChange(value: string): void {
    this.typedText.set(value);
  }

  onOpenInstantCheckup(instantCheckupId: string): void {
    this.router.navigate([`/user/instantCheckup/${instantCheckupId}`]);
  }

  onCloseClicked(): void {
    this.eventLogger.cleverTapEvent(CLICKED_BACK_EVENT, JSON.stringify({ name: 'home' }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
  }

  async onContinue(): Promise<void> {
    if (this.quizService.layout() === QuizQuestionLayout.PhotoUpload) {
      await this.openPhotoAction();
      return;
    }
    const message = this.buildAnswerMessage();
    if (!message) return;
    const questionNumber = this.quizService.questionNumber();
    const outcome = await this.quizService.submitAnswer(message);
    if (outcome !== QuizAnswerOutcome.Sent) return;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: `${ANALYTICS_PREFIX}_ans_q${questionNumber}` }));
  }

  /** Same dispatch as legacy chatV2's processAssistantAction — the shared SingleSelectorService owns the flow. */
  private async openPhotoAction(): Promise<void> {
    const option = this.quizService.photoCtaOption();
    const question = this.quizService.currentQuestion();
    if (!option || !question) return;
    await this.singleSelectorService.openInAppAction(
      option as unknown as InputType,
      (question.get('context') as Record<string, unknown>) || {},
      undefined,
      question.id,
      question.className || '',
    );
  }

  private buildAnswerMessage(): string {
    const layout = this.quizService.layout();
    if (layout === QuizQuestionLayout.Text) {
      return this.typedText().trim() || this.quizService.textCtaOption()?.text || '';
    }
    const options = this.allSelectableOptions();
    if (layout === QuizQuestionLayout.MultiSelect) {
      return this.selectedIndices()
        .map((index) => options[index]?.value || options[index]?.text || '')
        .filter((value) => !!value)
        .join(MULTI_SELECT_SEPARATOR);
    }
    const selected = options[this.selectedIndices()[0]];
    return selected?.text || selected?.value || '';
  }

  private buildQuizConfig(session: Table.ConsultationSession): QuizChatConfig {
    const concernClass: string = session.get('PrivateMainConcernClass');
    return {
      channel: {
        user: this.user,
        parent: session,
        parentField: CONSULTATION_SESSION_TYPE,
        fetchChats: (payload) => this.connectionService.findConsultationSessionChat(payload),
        replyUniqueIdPrefix: 'assistant_3_',
        pollingSeed: this.experimentService.getConsultationQuizBotReplySeed() ?? undefined,
        onBotReplyRetry: () => this.eventLogger.trackEvent('BOT_RESPONSE_RETRY', {
          userId: this.user?.id, assistant: this.quizService.currentQuestion(),
        }),
        onBotReplyReceived: (timeInMillSec: number) => this.eventLogger.trackEvent('BOT_RESPONSE_TIME', {
          userId: this.user?.id,
          timeInMillSec,
          lastResponseId: this.quizService.currentQuestion()?.id,
          lastQuestionId: this.quizService.currentQuestion()?.get('questionId'),
        }),
      },
      // The consultation channel only ever yields ConsultationSessionChat rows.
      isCompletionMessage: (message: QuizChatMessage) => this.consultationSessionService.isRegimenAllocationMessage(message as Table.ConsultationSessionChat),
      skipQuestionIds: [...AUTO_SKIP_QUESTION_IDS],
      resumeNudge: () => this.connectionService.switchUserToState(
        `v4_${concernClass.toLowerCase()}:`,
        { consultationSessionId: session.id },
      ),
    };
  }

  private async resolveVoiceVisibility(): Promise<void> {
    if (!this.experimentService.isChatSpeechToTextEnabled()) return;
    const osName = await this.appBridge.requestOSInformation();
    if (osName === IOS_OS_NAME) return;
    if (!this.appBridge.isSpeechToTextSupported()) return;
    this.showVoice.set(true);
  }

  private handleCompletion(): void {
    if (this.completionHandled) return;
    this.completionHandled = true;
    const session = this.consultationSession();
    const handled = this.consultationSessionService.handleRegimenAllocation(
      this.quizService.completionMessage() as Table.ConsultationSessionChat | null, this.user, session,
    );
    // Internal users skip the allocation side effects (chatV2 parity) but still leave the quiz.
    if (!handled) this.consultationSessionService.navigateToReport(session?.get('PrivateMainConcernClass'));
  }

  private navigateToLegacyChat(): void {
    const session = this.consultationSession();
    if (!session) return;
    this.router.navigate(['/chatV2', session.id], {
      queryParams: { type: CONSULTATION_SESSION_TYPE, fallback: true },
      replaceUrl: true,
    });
  }
}
